import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClockIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { connect } from "react-redux";

import * as actions from "../../../store/actions/index";
import { withRouter } from "../../../shared/utility";
import {
  QuickbooksLoginViewModel,
  type QuickBooksImportStatus,
  type QuickBooksImportType,
} from "../../../viewmodels/quickbooks/QuickBooksLoginViewModel";
import EmptyModal from "../../../components/modals/EmptyModal";
import type { AppDispatch, RootState } from "../../../index";

interface QuickbooksLoginProps {
  logout: () => void;
}

interface ImportCardProps {
  type: QuickBooksImportType;
  title: string;
  description: string;
  importStatus: QuickBooksImportStatus | null;
  loading: boolean;
  starting: boolean;
  onStart: (type: QuickBooksImportType) => void;
}

const statusPresentation = (status?: QuickBooksImportStatus["status"]) => {
  switch (status) {
    case "queued":
      return {
        label: "Queued",
        tone: "bg-amber-50 text-amber-700 ring-amber-200",
        Icon: ClockIcon,
      };
    case "running":
      return {
        label: "Importing",
        tone: "bg-blue-50 text-blue-700 ring-blue-200",
        Icon: ArrowPathIcon,
      };
    case "completed":
      return {
        label: "Completed",
        tone: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        Icon: CheckCircleIcon,
      };
    case "failed":
      return {
        label: "Needs attention",
        tone: "bg-red-50 text-red-700 ring-red-200",
        Icon: ExclamationTriangleIcon,
      };
    default:
      return {
        label: "Not imported",
        tone: "bg-slate-50 text-slate-600 ring-slate-200",
        Icon: ClockIcon,
      };
  }
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

function ImportResult({
  type,
  status,
}: {
  type: QuickBooksImportType;
  status: QuickBooksImportStatus;
}) {
  if (status.status === "failed") {
    return (
      <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm leading-6 text-red-700 ring-1 ring-inset ring-red-100">
        {status.error || "This import could not be completed. Please try again."}
      </p>
    );
  }

  if (status.status === "queued" || status.status === "running") {
    return (
      <p className="mt-4 text-sm leading-6 text-slate-500">
        Attempt {Math.max(status.attempts, 1)} of {status.max_attempts}. You can leave this page
        while the import runs.
      </p>
    );
  }

  if (status.status !== "completed" || !status.result) return null;

  const details =
    type === "customers"
      ? [
          ["Customers created", status.result.created ?? 0],
          ["Customers updated", status.result.updated ?? 0],
          ["Groups created", status.result.groups_created ?? 0],
          ["Groups updated", status.result.groups_updated ?? 0],
        ]
      : [
          ["Products created", status.result.created ?? 0],
          ["Products updated", status.result.updated ?? 0],
          ["Primary categories", status.result.categories?.primary ?? 0],
          ["Subcategories", status.result.categories?.sub ?? 0],
        ];

  return (
    <dl className="mt-5 grid grid-cols-2 gap-3">
      {details.map(([label, value]) => (
        <div key={label} className="rounded-xl bg-slate-50 px-3 py-3">
          <dt className="text-xs font-medium text-slate-500">{label}</dt>
          <dd className="mt-1 text-xl font-semibold text-slate-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ImportCard({
  type,
  title,
  description,
  importStatus,
  loading,
  starting,
  onStart,
}: ImportCardProps) {
  const { label, tone, Icon } = statusPresentation(importStatus?.status);
  const TypeIcon = type === "customers" ? UsersIcon : CubeIcon;
  const active = importStatus?.status === "queued" || importStatus?.status === "running";
  const timestamp = formatDate(importStatus?.finished_at || importStatus?.updated_at);

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <TypeIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${tone}`}
        >
          <Icon
            className={`h-4 w-4 ${importStatus?.status === "running" ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          {loading && !importStatus ? "Loading" : label}
        </span>
      </div>

      <div className="mt-5">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-2 min-h-12 text-sm leading-6 text-slate-500">{description}</p>
      </div>

      {importStatus && <ImportResult type={type} status={importStatus} />}

      <div className="mt-auto pt-6">
        {timestamp && <p className="mb-3 text-xs text-slate-400">Last activity {timestamp}</p>}
        <button
          type="button"
          onClick={() => onStart(type)}
          disabled={loading || starting || active}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowPathIcon
            className={`h-5 w-5 ${starting ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          {starting ? "Queueing…" : active ? "Import in progress" : `Import ${title.toLowerCase()}`}
        </button>
      </div>
    </article>
  );
}

function QuickbooksLogin({ logout }: QuickbooksLoginProps) {
  const {
    emptyModal,
    imports,
    loading,
    loadingImports,
    startingImport,
    setEmptyModelOpen,
    onLoginPress,
    onStartImport,
    refreshImports,
  } = QuickbooksLoginViewModel(logout);

  const handleLogin = async () => {
    const redirectUrl = await onLoginPress();
    if (redirectUrl) window.location.href = redirectUrl;
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-indigo-950 to-indigo-800 px-6 py-8 text-white shadow-lg sm:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-indigo-100 ring-1 ring-inset ring-white/15">
                QuickBooks Online
              </span>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight">
                Keep your portal data in sync
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-indigo-100">
                Connect your company, then send customer or product imports to the background.
                Imports continue safely if you leave this page.
              </p>
            </div>
            <button
              type="button"
              onClick={handleLogin}
              disabled={loading}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-indigo-900 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-900 disabled:opacity-60"
            >
              {loading ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
              ) : (
                <LinkIcon className="h-5 w-5" />
              )}
              {loading ? "Connecting…" : "Connect or reconnect"}
              {!loading && <ArrowTopRightOnSquareIcon className="h-4 w-4" />}
            </button>
          </div>
        </section>

        <section>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Data imports</h2>
              <p className="mt-1 text-sm text-slate-500">
                Each import is attempted up to three times. Its final result stays available here.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void refreshImports()}
              disabled={loadingImports}
              className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 sm:self-auto"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loadingImports ? "animate-spin" : ""}`} />
              Refresh status
            </button>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <ImportCard
              type="customers"
              title="Customers"
              description="Import customers and addresses. QuickBooks parent customers become portal customer groups."
              importStatus={imports.customers}
              loading={loadingImports}
              starting={startingImport === "customers"}
              onStart={onStartImport}
            />
            <ImportCard
              type="products"
              title="Products"
              description="Import products, primary categories, and subcategories from your QuickBooks catalog."
              importStatus={imports.products}
              loading={loadingImports}
              starting={startingImport === "products"}
              onStart={onStartImport}
            />
          </div>
        </section>

        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
          <p>
            Imports are fire-and-forget. Return to this page or use <strong>Refresh status</strong>{" "}
            to see whether an import completed or needs attention.
          </p>
        </div>
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (_state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  logout: () => dispatch(actions.logout()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(QuickbooksLogin));
